// Replaces letter-sphere.js. Scatters all 26 letters across city surfaces.
// Reuses the same letter interaction components (billboard, letter-hover, letter-click).

import { LETTER_COLORS } from '../constants.js';
import { disposeLetterEntity, refreshAllRaycasters } from '../utils.js';

// Static city positions for all 26 letters.
// Constraints:
// - All Z values positive (blink-controls negative Z teleport bug).
// - No rot field — billboard component overrides rotation every tick to face the camera.
// - Letters span front (z:2–8), mid (z:9–16), mid-back (z:17–22), and back (z:23–30) zones.
const LETTER_POSITIONS = [
  // Front zone — z: 2–8
  { pos: '-8.5 4 5',    scale: '1 1 1'     },  // A — building 1 right-wall face
  { pos: '-12 8 7.5',   scale: '1 1 1'     },  // B — building 1 back face
  { pos: '-12 14.5 5',  scale: '1.2 1.2 1' },  // C — building 1 rooftop
  { pos: '-3 3.5 4',    scale: '1 1 1'     },  // D — building 2 right face
  { pos: '-5 10.5 4',   scale: '1 1 1'     },  // E — building 2 rooftop
  { pos: '5.5 5 5',     scale: '1 1 1'     },  // F — building 3 left face
  { pos: '8 14 7',      scale: '1 1 1'     },  // G — building 3 back wall
  { pos: '8 18.5 5',    scale: '1.2 1.2 1' },  // H — building 3 rooftop
  { pos: '11.5 4 4',    scale: '1 1 1'     },  // I — building 4 left face

  // Mid zone — z: 9–16 (player spawns near z=15)
  { pos: '-1 2.5 10',   scale: '1 1 1'     },  // J — street-level alley sign
  { pos: '2 3 12',      scale: '1 1 1'     },  // K — street sign
  { pos: '-2 4 14',     scale: '1 1 1'     },  // L — near player spawn
  { pos: '3 2.5 16',    scale: '1 1 1'     },  // M — street

  // Mid-back zone — z: 17–22
  { pos: '-7.5 5 17',   scale: '1 1 1'     },  // N — building 5 right face
  { pos: '-11 16.5 17', scale: '1.2 1.2 1' },  // O — building 5 rooftop
  { pos: '-2 4 19',     scale: '1 1 1'     },  // P — building 6 right face
  { pos: '-4 12.5 19',  scale: '1 1 1'     },  // Q — building 6 rooftop
  { pos: '4.5 6 21',    scale: '1 1 1'     },  // R — building 7 left face
  { pos: '7 14.5 21',   scale: '1.2 1.2 1' },  // S — building 7 rooftop
  { pos: '10.5 4 18',   scale: '1 1 1'     },  // T — building 8 left face

  // Back zone — z: 23–30
  { pos: '0 3 24',      scale: '1 1 1'     },  // U — far street
  { pos: '-5.5 7 27',   scale: '1 1 1'     },  // V — building 9 right face
  { pos: '-8 20.5 27',  scale: '1.2 1.2 1' },  // W — building 9 rooftop
  { pos: '3.5 5 28',    scale: '1 1 1'     },  // X — building 10 left face
  { pos: '6 15.5 28',   scale: '1 1 1'     },  // Y — building 10 rooftop
  { pos: '-1 2 29',     scale: '1 1 1'     },  // Z — far back alley
];

AFRAME.registerComponent('letter-city', {
  init: function () {
    // Authoritative list for cleanup — do NOT use querySelectorAll('.clickable-letter').
    // Letters mid-explosion have had 'clickable-letter' removed from their classList;
    // a class query would silently miss them and leave dangling entities.
    this._letters = [];

    LETTER_POSITIONS.forEach((cfg, i) => {
      const el = this._spawnLetter(cfg, i);
      this._letters.push(el);
    });

    // Raycasters may have registered their target objects before letter-city appended
    // its children. Refresh after all 26 entities exist.
    refreshAllRaycasters(this.el.sceneEl);

    console.log('VR Letter Explorer: city initialized with ' + LETTER_POSITIONS.length + ' letters');
  },

  _spawnLetter: function (cfg, index) {
    const letter = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[index];
    const color  = LETTER_COLORS[index % LETTER_COLORS.length];

    const entity = document.createElement('a-entity');
    entity.setAttribute('position', cfg.pos);
    entity.setAttribute('scale', cfg.scale);
    entity.setAttribute('text', {
      value:  letter,
      color:  color,
      align:  'center',
      width:  8,
      font:   'mozillavr',
      shader: 'sdf',
    });
    entity.dataset.letter = letter;
    entity.dataset.color  = color;
    entity.classList.add('clickable-letter');
    entity.setAttribute('billboard', '');
    entity.setAttribute('letter-hover', '');
    entity.setAttribute('letter-click', '');

    this.el.appendChild(entity);
    return entity;
  },

  remove: function () {
    this._letters.forEach(el => {
      if (el.parentNode) { disposeLetterEntity(el); }
    });
    this._letters = [];
  },
});

if (import.meta.hot) { import.meta.hot.decline(); }
